import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';

const UserVerifications = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/verifications/me`);
        setList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Fetch verifications error:', err);
        setList([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byEvent = list.reduce((acc, v) => {
    const key = v.event_id != null ? `event_${v.event_id}` : 'legacy';
    const label = v.event_id != null && v.event_name ? v.event_name : 'Legacy / No event';
    if (!acc[key]) acc[key] = { event_id: v.event_id, event_name: label, items: [] };
    acc[key].items.push(v);
    return acc;
  }, {});
  const allGroups = Object.values(byEvent);
  const filterValue = eventFilter === 'all' ? null : eventFilter === 'legacy' ? 'legacy' : eventFilter;
  const groups = filterValue === null
    ? allGroups
    : filterValue === 'legacy'
      ? allGroups.filter((g) => g.event_id == null)
      : allGroups.filter((g) => g.event_id != null && g.event_id === Number(filterValue));

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading verifications…</p>
        </div>
      </div>
    );
  }

  const totalVerifications = list.length;
  const totalMatches = list.reduce((sum, v) => sum + (v.matches_found || 0), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-5">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Verifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {totalVerifications === 0
              ? 'No verifications yet'
              : `${totalVerifications} verification${totalVerifications !== 1 ? 's' : ''} · ${totalMatches} total matches`}
          </p>
        </div>
        {allGroups.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="event-filter" className="text-xs font-semibold text-slate-500 whitespace-nowrap">Filter:</label>
            <select
              id="event-filter"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition shadow-sm"
            >
              <option value="all">All events</option>
              {allGroups.map((g) => (
                <option key={g.event_id ?? 'legacy'} value={g.event_id ?? 'legacy'}>
                  {g.event_name} ({g.items.length})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Empty state */}
      {groups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
            <svg className="w-8 h-8 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-800 mb-1">No verifications yet</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Guests use your event&apos;s &ldquo;Find my photos&rdquo; link to verify their face. Results will appear here grouped by event.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.event_id ?? 'legacy'} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

              {/* Group header */}
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-slate-800">{group.event_name}</h3>
                  {group.event_id == null && (
                    <p className="text-xs text-amber-600 mt-0.5">From an older verify link — new verifications appear under the event name.</p>
                  )}
                </div>
                <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium">
                  {group.items.length} verification{group.items.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Photo grid */}
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {group.items.map((v) => (
                  <div
                    key={v.id}
                    className="rounded-xl border border-slate-200 overflow-hidden hover:shadow-md hover:border-violet-200 transition-all cursor-pointer group"
                    onClick={() => setSelected(v)}
                  >
                    <div className="relative aspect-square bg-slate-100">
                      <img
                        src={`${API_URL}/verifications/${v.id}/thumbnail`}
                        alt="Verification"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          if (e.target.src !== `${API_URL}/verifications/${v.id}/image`) {
                            e.target.src = `${API_URL}/verifications/${v.id}/image`;
                          } else if (v.filepath) {
                            e.target.src = `${API_BASE_URL}/${v.filepath}`;
                          }
                        }}
                      />
                      {/* Match badge */}
                      <div className="absolute top-1.5 right-1.5">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${v.matches_found > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-600/80 text-white'}`}>
                          {v.matches_found > 0 ? `${v.matches_found} ✓` : '0'}
                        </span>
                      </div>
                    </div>
                    <div className="px-2 py-1.5">
                      <p className="text-[11px] font-medium text-slate-600 truncate">{v.filename}</p>
                      <p className="text-[10px] text-slate-400">{new Date(v.created_at).toLocaleDateString(undefined, { dateStyle: 'short' })}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${API_URL}/verifications/${selected.id}/image`}
              alt={selected.filename}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
            <div className="mt-3 flex items-center justify-between gap-4">
              <div className="text-white">
                <p className="text-sm font-medium">{selected.filename}</p>
                <p className="text-xs text-white/70">{selected.matches_found} match{selected.matches_found !== 1 ? 'es' : ''} found</p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserVerifications;
